import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import Layout from './Layout';
import { useTranslation } from 'react-i18next';

const EMPTY_FORM = {
  customer: '',
  recipient: '',
  recipient_name: '',
  recipient_phone: '',
  pickup_address: '',
  delivery_address: '',
  scheduled_pickup: '',
  scheduled_delivery: '',
  status: 'pending',
  priority: 'normal',
  description: '',
  weight: '',
  cod_amount: '',
  cod_currency: 'USD',
  delivery_fee: '',
  delivery_fee_currency: 'USD',
  hide_customer_phones_for_driver: false,
  notes: '',
};

const LEBANON_ADDRESS_PRESETS = [
  'Beirut - Hamra',
  'Beirut - Achrafieh',
  'Beirut - Verdun',
  'Beirut - Ras Beirut',
  'Beirut - Tariq El Jdideh',
  'Beirut - Mar Elias',
  'Beirut - Bachoura',
  'Beirut - Gemmayzeh',
  'Beirut - Mar Mikhael',
  'Beirut - Badaro',
  'Metn - Jdeideh',
  'Metn - Dekwaneh',
  'Metn - Sin El Fil',
  'Metn - Antelias',
  'Metn - Jal El Dib',
  'Baabda - Hazmieh',
  'Baabda - Hadath',
  'Baabda - Baabda',
  'Aley - Aley',
  'Aley - Bchamoun',
  'Aley - Choueifat',
  'Chouf - Damour',
  'Keserwan - Jounieh',
  'Keserwan - Zouk Mosbeh',
  'Keserwan - Adma',
  'Tripoli - Mina',
  'Tripoli - Tal',
  'Tripoli - Abi Samra',
  'Zgharta - Zgharta',
  'Koura - Amioun',
  'Batroun - Batroun',
  'Byblos - Jbeil',
  'Akkar - Halba',
  'Saida - Saida',
  'Saida - Abra',
  'Tyre - Sour',
  'Nabatieh - Nabatieh',
  'Jezzine - Jezzine',
  'Baalbek - Baalbek',
  'Zahle - Zahle',
  'West Bekaa - Joub Jannine',
  'Rachaya - Rachaya',
  'Hermel - Hermel',
];

export default function Orders() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [allOrders, setAllOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [officePhoto, setOfficePhoto] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [assignOrderId, setAssignOrderId] = useState('');
  const [assignDriverId, setAssignDriverId] = useState('');
  const [exchangeRate, setExchangeRate] = useState(89000);
  const formCardRef = useRef(null);

  const toDateTimeLocal = useCallback((value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);

  const normalizeMoneyInput = useCallback((value) => {
    const raw = String(value || '').replace(/,/g, '').replace(/[^0-9.]/g, '');
    const parts = raw.split('.');
    if (parts.length <= 1) return raw;
    return `${parts[0]}.${parts.slice(1).join('')}`;
  }, []);

  const withThousands = useCallback((value) => {
    const normalized = normalizeMoneyInput(value);
    if (!normalized) return '';
    const parts = normalized.split('.');
    const intPart = parts[0].replace(/^0+(\d)/, '$1');
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (parts.length === 1) return formattedInt;
    return `${formattedInt}.${parts[1]}`;
  }, [normalizeMoneyInput]);

  const load = useCallback(async () => {
    const [o, c, d, p] = await Promise.all([
      API.get('orders/').catch(() => ({ data: [] })),
      API.get('customers/').catch(() => ({ data: [] })),
      API.get('drivers/').catch(() => ({ data: [] })),
      API.get('profile/me/').catch(() => ({ data: {} })),
    ]);

    setAllOrders(Array.isArray(o.data) ? o.data : []);
    setCustomers(Array.isArray(c.data) ? c.data : []);
    setDrivers(Array.isArray(d.data) ? d.data : []);
    setExchangeRate(Number(p?.data?.exchange_rate_usd_to_lbp || 89000));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (showForm) {
      formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showForm, editing]);

  const filtered = useMemo(() => {
    const statusRank = (status) => {
      if (status === 'pending') return 0;
      if (status === 'offered') return 1;
      if (status === 'assigned') return 2;
      return 3;
    };

    return allOrders.filter((o) => {
      const text = `${o.order_number} ${o.customer_name || ''} ${o.recipient_name || ''} ${o.driver_name || ''} ${o.delivery_address || ''}`.toLowerCase();
      const okText = text.includes(deferredQuery.toLowerCase());
      const okStatus = statusFilter ? o.status === statusFilter : true;
      return okText && okStatus;
    }).sort((a, b) => {
      const rankDiff = statusRank(a.status) - statusRank(b.status);
      if (rankDiff !== 0) return rankDiff;
      const aTime = new Date(a.created_at || 0).getTime();
      const bTime = new Date(b.created_at || 0).getTime();
      return aTime - bTime;
    });
  }, [allOrders, deferredQuery, statusFilter]);

  const orderSuggestions = useMemo(() => {
    const q = query.toLowerCase().trim();
    const base = q
      ? allOrders.filter((o) => `${o.order_number} ${o.customer_name || ''} ${o.recipient_name || ''} ${o.driver_name || ''} ${o.delivery_address || ''} ${o.barcode || ''}`.toLowerCase().includes(q))
      : allOrders;
    return base.slice(0, 30).map((o) => `${o.order_number} - ${o.customer_name || ''} - ${o.delivery_address || ''}`);
  }, [allOrders, query]);

  const knownAddresses = useMemo(() => {
    const set = new Set();
    LEBANON_ADDRESS_PRESETS.forEach((addr) => set.add(addr));
    customers.forEach((c) => {
      if (c.address) set.add(String(c.address).trim());
    });
    allOrders.forEach((o) => {
      if (o.pickup_address) set.add(String(o.pickup_address).trim());
      if (o.delivery_address) set.add(String(o.delivery_address).trim());
    });
    return Array.from(set).filter(Boolean).slice(0, 500);
  }, [allOrders, customers]);

  const parseCoordinatesFromText = useCallback((rawText) => {
    const text = String(rawText || '').trim();
    if (!text) return null;

    const byXY = text.match(/X\s*:\s*(-?\d+(?:\.\d+)?)\s*[,;]?\s*Y\s*:\s*(-?\d+(?:\.\d+)?)/i);
    if (byXY) {
      const lat = Number(byXY[1]);
      const lng = Number(byXY[2]);
      if (!Number.isNaN(lat) && !Number.isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        return { lat, lng };
      }
    }

    const byQuery = text.match(/[?&](?:q|query|ll)=(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/i);
    if (byQuery) {
      const lat = Number(byQuery[1]);
      const lng = Number(byQuery[2]);
      if (!Number.isNaN(lat) && !Number.isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        return { lat, lng };
      }
    }

    const genericPair = text.match(/(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)/);
    if (genericPair) {
      const lat = Number(genericPair[1]);
      const lng = Number(genericPair[2]);
      if (!Number.isNaN(lat) && !Number.isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        return { lat, lng };
      }
    }

    return null;
  }, []);

  const reverseGeocode = useCallback(async (lat, lng) => {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&accept-language=ar,en`;
    try {
      const res = await fetch(url, {
        headers: {
          Accept: 'application/json',
        },
      });
      const data = await res.json();
      return data?.display_name || '';
    } catch (_) {
      return '';
    }
  }, []);

  const applyCoordinatesToField = useCallback(async (fieldName, lat, lng) => {
    const latFixed = Number(lat).toFixed(6);
    const lngFixed = Number(lng).toFixed(6);
    const coordsText = `X:${latFixed}, Y:${lngFixed}`;
    const place = await reverseGeocode(latFixed, lngFixed);
    const nextValue = place ? `${place} (${coordsText})` : coordsText;
    setForm((prev) => ({ ...prev, [fieldName]: nextValue }));
  }, [reverseGeocode]);

  const setAddressFromPhoneLocation = useCallback((fieldName) => {
    if (!navigator.geolocation) {
      window.alert(t('Geolocation not supported on this device'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        await applyCoordinatesToField(fieldName, pos.coords.latitude, pos.coords.longitude);
      },
      () => {
        window.alert(t('Could not access phone location'));
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  }, [applyCoordinatesToField, t]);

  const setAddressFromWhatsAppLocation = useCallback(async (fieldName) => {
    const raw = window.prompt(t('Paste WhatsApp location or coordinates'));
    if (!raw) return;
    const parsed = parseCoordinatesFromText(raw);
    if (!parsed) {
      window.alert(t('Could not parse coordinates from the pasted text'));
      return;
    }
    await applyCoordinatesToField(fieldName, parsed.lat, parsed.lng);
  }, [applyCoordinatesToField, parseCoordinatesFromText, t]);

  const save = async (e) => {
    e.preventDefault();

    const payload = new FormData();
    Object.entries({
      ...form,
      recipient: form.recipient || '',
      weight: form.weight || '',
      cod_amount: normalizeMoneyInput(form.cod_amount) || '',
      cod_currency: form.cod_currency || 'USD',
      delivery_fee: normalizeMoneyInput(form.delivery_fee) || '',
      delivery_fee_currency: form.delivery_fee_currency || 'USD',
      scheduled_pickup: form.scheduled_pickup || '',
      scheduled_delivery: form.scheduled_delivery || '',
      hide_customer_phones_for_driver: Boolean(form.hide_customer_phones_for_driver),
    }).forEach(([key, value]) => {
      payload.append(key, value ?? '');
    });
    if (officePhoto) {
      payload.append('office_photo', officePhoto);
    }

    if (editing) {
      await API.put(`orders/${editing.id}/`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).catch(() => {});
    } else {
      await API.post('orders/', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).catch(() => {});
    }

    setEditing(null);
    setForm(EMPTY_FORM);
    setOfficePhoto(null);
    setShowForm(false);
    load();
  };

  const openEdit = useCallback((order) => {
    setEditing(order);
    setShowForm(true);
    setOfficePhoto(null);
    setForm({
      customer: order.customer || '',
      recipient: order.recipient || '',
      recipient_name: order.recipient_name || '',
      recipient_phone: order.recipient_phone || '',
      pickup_address: order.pickup_address || '',
      delivery_address: order.delivery_address || '',
      scheduled_pickup: toDateTimeLocal(order.scheduled_pickup),
      scheduled_delivery: toDateTimeLocal(order.scheduled_delivery),
      status: order.status || 'pending',
      priority: order.priority || 'normal',
      description: order.description || '',
      weight: order.weight || '',
      cod_amount: order.cod_amount || '',
      cod_currency: order.cod_currency || 'USD',
      delivery_fee: order.delivery_fee || '',
      delivery_fee_currency: order.delivery_fee_currency || 'USD',
      hide_customer_phones_for_driver: Boolean(order.hide_customer_phones_for_driver),
      notes: order.notes || '',
    });
  }, [toDateTimeLocal]);

  const remove = useCallback(async (order) => {
    const confirmed = window.confirm(t('Are you sure you want to delete this order?'));
    if (!confirmed) return;
    await API.delete(`orders/${order.id}/`).catch((err) => {
      const msg = err?.response?.data?.error || t('Delete failed');
      window.alert(msg);
    });
    load();
  }, [load, t]);

  const changeStatus = useCallback(async (orderId, status) => {
    await API.post(`orders/${orderId}/update_status/`, { status }).catch(() => {});
    load();
  }, [load]);

  const changePaymentStatus = useCallback(async (orderId, paymentStatus) => {
    await API.post(`orders/${orderId}/update-payment-status/`, { payment_status: paymentStatus }).catch((err) => {
      const msg = err?.response?.data?.error || t('Update failed');
      window.alert(msg);
    });
    load();
  }, [load, t]);

  const assignDriver = async () => {
    if (!assignOrderId || !assignDriverId) return;
    await API.post(`orders/${assignOrderId}/assign-driver/`, { driver_id: assignDriverId }).catch(() => {});
    setAssignOrderId('');
    setAssignDriverId('');
    load();
  };

  const statusLabel = useCallback((status) => {
    const mapped = t(`status.${status}`);
    return mapped === `status.${status}` ? t(status) : mapped;
  }, [t]);
  const paymentStatusLabel = useCallback((status) => {
    const mapped = t(`payment.${status}`);
    return mapped === `payment.${status}` ? status : mapped;
  }, [t]);
  const formatMoney = useCallback((amount, currency) => {
    if (amount === null || amount === undefined || amount === '') return '-';
    return `${withThousands(String(amount))} ${currency || 'USD'}`;
  }, [withThousands]);
  const codInLbp = useCallback((order) => {
    const amount = Number(order.cod_amount || 0);
    if (!amount) return null;
    if (order.cod_currency === 'LBP') return amount;
    return Math.round(amount * exchangeRate);
  }, [exchangeRate]);
  const onlineDrivers = drivers.filter((d) => d.driver_status === 'online');

  const ordersTable = useMemo(() => (
    <div className="card table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t('Order')}</th>
            <th>{t('Barcode')}</th>
            <th>{t('Sender')}</th>
            <th>{t('Recipient')}</th>
            <th>{t('Driver')}</th>
            <th>{t('Status')}</th>
            <th>{t('Money')}</th>
            <th>{t('Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((o) => (
            <tr key={o.id}>
              <td>{o.order_number}</td>
              <td>{o.barcode || '-'}</td>
              <td>{o.customer_name}</td>
              <td>{o.recipient_name || '-'}</td>
              <td>{o.driver_name || '-'}</td>
              <td><span className={`badge ${o.status}`}>{statusLabel(o.status)}</span></td>
              <td>
                <div className="mini-meta">COD: {formatMoney(o.cod_amount, o.cod_currency)}</div>
                <div className="mini-meta">Fee: {formatMoney(o.delivery_fee, o.delivery_fee_currency)}</div>
                <div className="mini-meta">{t('Payment')}: {paymentStatusLabel(o.payment_status || 'not_required')}</div>
                {codInLbp(o) ? <div className="mini-meta">LBP: {withThousands(String(codInLbp(o)))}</div> : null}
                <select className="input" value={o.payment_status || 'not_required'} onChange={(e) => changePaymentStatus(o.id, e.target.value)}>
                  <option value="not_required">{paymentStatusLabel('not_required')}</option>
                  <option value="pending_collection">{paymentStatusLabel('pending_collection')}</option>
                  <option value="collected_from_recipient">{paymentStatusLabel('collected_from_recipient')}</option>
                  <option value="received_by_office">{paymentStatusLabel('received_by_office')}</option>
                  <option value="remitted_to_sender">{paymentStatusLabel('remitted_to_sender')}</option>
                </select>
              </td>
              <td>
                <div className="row-actions">
                  <button className="btn btn-secondary" type="button" onClick={() => openEdit(o)}>{t('Edit')}</button>
                  <button className="btn btn-secondary" type="button" onClick={() => navigate(`/orders/print/${o.id}`)}>{t('Print')}</button>
                  <button className="btn btn-danger" type="button" onClick={() => remove(o)}>{t('Delete')}</button>
                  <select className="input" value={o.status} onChange={(e) => changeStatus(o.id, e.target.value)}>
                    <option value="pending">{statusLabel('pending')}</option>
                    <option value="offered">{statusLabel('offered')}</option>
                    <option value="assigned">{statusLabel('assigned')}</option>
                    <option value="picked_up">{statusLabel('picked_up')}</option>
                    <option value="in_transit">{statusLabel('in_transit')}</option>
                    <option value="delivered">{statusLabel('delivered')}</option>
                    <option value="cancelled">{statusLabel('cancelled')}</option>
                  </select>
                </div>
              </td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr>
              <td colSpan={8}>{t('No orders found')}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  ), [filtered, t, formatMoney, paymentStatusLabel, codInLbp, changePaymentStatus, navigate, remove, changeStatus, statusLabel, withThousands, openEdit]);

  return (
    <Layout title={t('Orders')}>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="filters-row">
          <input
            className="input"
            list="orders-search-suggestions"
            placeholder={t('Search Orders')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <datalist id="orders-search-suggestions">
            {orderSuggestions.map((item) => (
              <option key={item} value={item} />
            ))}
          </datalist>

          <select className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('All Status')}</option>
            <option value="pending">{statusLabel('pending')}</option>
            <option value="offered">{statusLabel('offered')}</option>
            <option value="assigned">{statusLabel('assigned')}</option>
            <option value="picked_up">{statusLabel('picked_up')}</option>
            <option value="in_transit">{statusLabel('in_transit')}</option>
            <option value="delivered">{statusLabel('delivered')}</option>
            <option value="cancelled">{statusLabel('cancelled')}</option>
          </select>

          <button
            className="btn btn-primary"
            type="button"
            onClick={() => {
              setEditing(null);
              setForm(EMPTY_FORM);
              setOfficePhoto(null);
              setShowForm((prev) => !prev);
            }}
          >
            {showForm && !editing ? t('Hide Add Form') : t('Add Order')}
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => setShowAssign((prev) => !prev)}
          >
            {showAssign ? t('Hide assignment box') : t('Offer Order To Driver')}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }} ref={formCardRef}>
          <h3 style={{ marginBottom: 10 }}>{editing ? t('Edit Order') : t('Add Order')}</h3>
          <form onSubmit={save} className="form-grid">
          <select className="input" value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} required>
            <option value="">{t('Select sender')}</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.phone})</option>)}
          </select>

          <select className="input" value={form.recipient} onChange={(e) => setForm({ ...form, recipient: e.target.value })}>
            <option value="">{t('Select recipient optional')}</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.phone})</option>)}
          </select>

          <input className="input" placeholder={t('Recipient name')} value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} required />
          <input className="input" placeholder={t('Recipient phone')} value={form.recipient_phone} onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} required />

          <div>
            <label className="field-label">{t('Pickup Address')}</label>
            <input
              className="input"
              list="pickup-address-options"
              placeholder={t('Search or select pickup address')}
              value={form.pickup_address}
              onChange={(e) => setForm({ ...form, pickup_address: e.target.value })}
              required
            />
            <datalist id="pickup-address-options">
              {knownAddresses.map((addr) => <option key={`pickup-${addr}`} value={addr} />)}
            </datalist>
            <div className="row-actions" style={{ marginTop: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setAddressFromPhoneLocation('pickup_address')}>
                {t('Use phone location X,Y')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setAddressFromWhatsAppLocation('pickup_address')}>
                {t('Paste WhatsApp location')}
              </button>
            </div>
          </div>

          <div>
            <label className="field-label">{t('Delivery Address')}</label>
            <input
              className="input"
              list="delivery-address-options"
              placeholder={t('Search or select delivery address')}
              value={form.delivery_address}
              onChange={(e) => setForm({ ...form, delivery_address: e.target.value })}
              required
            />
            <datalist id="delivery-address-options">
              {knownAddresses.map((addr) => <option key={`delivery-${addr}`} value={addr} />)}
            </datalist>
            <div className="row-actions" style={{ marginTop: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setAddressFromPhoneLocation('delivery_address')}>
                {t('Use phone location X,Y')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setAddressFromWhatsAppLocation('delivery_address')}>
                {t('Paste WhatsApp location')}
              </button>
            </div>
          </div>

          <div>
            <label className="field-label">{t('Scheduled Pickup')}</label>
            <input className="input" type="datetime-local" value={form.scheduled_pickup || ''} onChange={(e) => setForm({ ...form, scheduled_pickup: e.target.value })} />
          </div>

          <div>
            <label className="field-label">{t('Scheduled Delivery')}</label>
            <input className="input" type="datetime-local" value={form.scheduled_delivery || ''} onChange={(e) => setForm({ ...form, scheduled_delivery: e.target.value })} />
          </div>

          <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="pending">{statusLabel('pending')}</option>
            <option value="offered">{statusLabel('offered')}</option>
            <option value="assigned">{statusLabel('assigned')}</option>
            <option value="picked_up">{statusLabel('picked_up')}</option>
            <option value="in_transit">{statusLabel('in_transit')}</option>
            <option value="delivered">{statusLabel('delivered')}</option>
            <option value="cancelled">{statusLabel('cancelled')}</option>
          </select>

          <select className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            <option value="low">{t('Low')}</option>
            <option value="normal">{t('Normal')}</option>
            <option value="high">{t('High')}</option>
            <option value="urgent">{t('Urgent')}</option>
          </select>

          <input className="input" placeholder={t('Weight kg')} value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} />
          <input className="input" placeholder={t('COD amount')} value={withThousands(form.cod_amount)} onChange={(e) => setForm({ ...form, cod_amount: normalizeMoneyInput(e.target.value) })} />
          <select className="input" value={form.cod_currency} onChange={(e) => setForm({ ...form, cod_currency: e.target.value })}>
            <option value="USD">USD</option>
            <option value="LBP">LBP</option>
          </select>
          <input className="input" placeholder={t('Delivery fee')} value={withThousands(form.delivery_fee)} onChange={(e) => setForm({ ...form, delivery_fee: normalizeMoneyInput(e.target.value) })} />
          <select className="input" value={form.delivery_fee_currency} onChange={(e) => setForm({ ...form, delivery_fee_currency: e.target.value })}>
            <option value="USD">USD</option>
            <option value="LBP">LBP</option>
          </select>
          <label className="inline-check">
            <input type="checkbox" checked={Boolean(form.hide_customer_phones_for_driver)} onChange={(e) => setForm({ ...form, hide_customer_phones_for_driver: e.target.checked })} />
            <span>{t('Hide phones for this order')}</span>
          </label>
          <input className="input" placeholder={t('Description')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

          <textarea className="input" placeholder={t('Notes')} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div>
            <label className="field-label">{t('Office photo before dispatch')}</label>
            <input className="input" type="file" accept="image/*" onChange={(e) => setOfficePhoto(e.target.files?.[0] || null)} />
          </div>

          <div className="row-actions">
            <button className="btn btn-primary" type="submit">{editing ? t('Update') : t('Create')}</button>
            <button className="btn btn-secondary" type="button" onClick={() => { setEditing(null); setForm(EMPTY_FORM); setOfficePhoto(null); setShowForm(false); }}>{t('Cancel')}</button>
          </div>
          </form>
        </div>
      )}

      {showAssign && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 10 }}>{t('Offer Order To Driver')}</h3>
          <div className="filters-row">
          <select className="input" value={assignOrderId} onChange={(e) => setAssignOrderId(e.target.value)}>
            <option value="">{t('Select pending order')}</option>
            {allOrders.filter((o) => o.status === 'pending').map((o) => (
              <option key={o.id} value={o.id}>{o.order_number} - {o.delivery_address}</option>
            ))}
          </select>

          <select className="input" value={assignDriverId} onChange={(e) => setAssignDriverId(e.target.value)}>
            <option value="">{t('Select online driver')}</option>
            {onlineDrivers.map((d) => (
              <option key={d.id} value={d.id}>{d.name} ({d.phone})</option>
            ))}
          </select>

          <button className="btn btn-success" type="button" onClick={assignDriver}>{t('Offer')}</button>
          </div>
        </div>
      )}

      {ordersTable}
    </Layout>
  );
}
